<template>
  <div class="flex">
    <div class="flex items-center" v-if="!editMealPlan">
      <DeleteConfirmation v-model="showDeleteConfirmation" :isSiteConfig="isSiteConfig" />

      <Tag
        v-if="isSiteConfig && hasMealPlan && mealPlanHealthEnabled && healthStatus"
        :severity="healthSeverity"
        :icon="healthIcon"
        :title="healthTooltip"
        class="mr-3"
        data-testid="meal-plan-health-status"
      >
        {{ healthStatusText }}
      </Tag>

      <Button
        id="view-meal-plan-health"
        outlined
        severity="secondary"
        :label="t('sites.singleSite.financialConfig.mealPlanHealth.title')"
        class="mr-4"
        v-if="isSiteConfig && hasMealPlan && mealPlanHealthEnabled"
        @click.stop="goToMealPlanHealth"
      >
        <template #icon>
          <i class="material-symbols-rounded text-[1.5rem] mr-2">monitor_heart</i>
        </template>
      </Button>

      <Button
        id="delete-meal-plan"
        severity="danger"
        outlined
        :label="t('sites.singleSite.financialConfig.mealPlans.actions.delete')"
        class="mr-4"
        @click.stop="openDeleteConfirmation"
        v-if="hasMealPlan"
      >
        <template #icon>
          <i class="material-symbols-rounded text-[1.5rem] mr-2">delete</i>
        </template>
      </Button>

      <IconButton id="edit-meal-plan" class="c-icon-button" @click.stop="editMealPlanMethod">
        <template #icon>
          <i class="material-symbols-rounded text-[1.5rem]">edit</i>
        </template>
      </IconButton>
    </div>

    <div class="flex" v-else>
      <Button
        id="cancel-edit-meal-plan"
        outlined
        severity="secondary"
        class="mr-4"
        :disabled="saving"
        :label="t('sites.singleSite.financialConfig.mealPlans.actions.cancel')"
        @click.stop="cancelMealPlanMethod"
      />

      <Button
        id="save-edit-meal-plan"
        :label="t('sites.singleSite.financialConfig.mealPlans.actions.save')"
        :loading="saving"
        @click.stop="saveEditMealPlan"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useEventBus } from '@vueuse/core';
import { useRoute, useRouter } from 'vue-router';
import { Tag } from '@centric-os/components/src/core/misc/tags/tag';
import { useSplitIoStore } from '@centric-os/stores/splitio/use-split-io';
import { FeatureFlags } from '@centric-os/types';
import { useFormStore } from '@centric-os/stores/form/use-form-store';
import { useStoreAPIAction } from '@centric-os/stores/plugins/api';
import { useMealPlan } from '../composables';
import DeleteConfirmation from '../delete-confirmation/DeleteConfirmation.vue';
import { useMealPlanStore } from '../../../../../routes/stores/use-meal-plan-store';
import { useFinancialConfigStore } from '../../../../../routes';
import { useMealPlanHealthStore } from '../../../../../routes/stores/use-meal-plan-health-store';
import { SitesRouteNames } from '../../../../../enums';
import { FinancialFormIDs } from '../../enums';

// Define props
const props = defineProps({
  isSiteConfig: {
    type: Boolean,
    required: true,
  },
});

// Ref for show delete confirmation
const showDeleteConfirmation = ref<boolean>(false);

// Set localization
const { t } = useI18n();

// Set meal plan store
const mealPlanStore = useMealPlanStore();
const { loading: saving } = useStoreAPIAction(mealPlanStore, 'saveMealPlan');

// Set financial config store
const fConfigStore = useFinancialConfigStore();
const { editMealPlan } = storeToRefs(fConfigStore);

// Set meal plan composable
const { hasMealPlan } = useMealPlan();

const route = useRoute();
const router = useRouter();

const { isFeatureOn } = useSplitIoStore();
const mealPlanHealthEnabled = computed<boolean>(() => isFeatureOn(FeatureFlags.MEALPLAN_HEALTH));

const healthStore = useMealPlanHealthStore('mealPlanHealthSummary');
const { summary: healthSummary } = storeToRefs(healthStore);
const healthStatus = computed<string | null>(() => healthSummary.value?.status ?? null);

const HEALTH_STATUS_TEXT: Record<string, () => string> = {
  healthy: () => t('sites.singleSite.financialConfig.mealPlanHealth.status.healthy'),
  attention: () => t('sites.singleSite.financialConfig.mealPlanHealth.status.attention'),
  critical: () => t('sites.singleSite.financialConfig.mealPlanHealth.status.critical'),
  no_traffic: () => t('sites.singleSite.financialConfig.mealPlanHealth.status.no_traffic'),
};

const HEALTH_STATUS_NOTE: Record<string, () => string> = {
  healthy: () => t('sites.singleSite.financialConfig.mealPlanHealth.statusNote.healthy'),
  attention: () => t('sites.singleSite.financialConfig.mealPlanHealth.statusNote.attention'),
  critical: () => t('sites.singleSite.financialConfig.mealPlanHealth.statusNote.critical'),
  no_traffic: () => t('sites.singleSite.financialConfig.mealPlanHealth.statusNote.no_traffic'),
};

const healthStatusText = computed<string>(
  () => HEALTH_STATUS_TEXT[healthStatus.value ?? '']?.() ?? '',
);

const healthTooltip = computed<string>(
  () => HEALTH_STATUS_NOTE[healthStatus.value ?? '']?.() ?? '',
);

const healthSeverity = computed<string>(() => {
  switch (healthStatus.value) {
    case 'healthy':
      return 'success';
    case 'attention':
      return 'warning';
    case 'critical':
      return 'danger';
    default:
      return 'secondary';
  }
});

const healthIcon = computed<string | undefined>(() => {
  switch (healthStatus.value) {
    case 'healthy':
      return 'check';
    case 'critical':
      return 'warning';
    default:
      return undefined;
  }
});

const goToMealPlanHealth = (): void => {
  void router.push({
    name: SitesRouteNames.SITE_MEAL_PLAN_HEALTH,
    params: { siteId: route.params.siteId },
  });
};

onMounted(async () => {
  if (!props.isSiteConfig || !mealPlanHealthEnabled.value || !hasMealPlan.value) return;

  const siteId = String(route.params.siteId ?? '');
  if (!siteId) return;

  try {
    await healthStore.fetchSummary(siteId);
  } catch {
    // Status is a nicety on this card; the page reports the error properly.
  }
});

// Set form store
const formStore = useFormStore();
const form = formStore.getForm(FinancialFormIDs.MEAL_PLAN_FORM);

// Set edit meal plan method
const editMealPlanMethod = (): void => {
  editMealPlan.value = true;
};

// Cancel edit meal plan method
const cancelMealPlanMethod = (): void => {
  editMealPlan.value = false;
  form.value?.resetForm();
};

// Set event bus for saving meal plan
const { emit: saveEdit } = useEventBus<void>(
  `save-meal-plan-${props.isSiteConfig ? 'site' : 'station'}`,
);

// Save edit meal plan method
const saveEditMealPlan = (): void => {
  saveEdit();
};

// Method to show delete confirmation
const openDeleteConfirmation = (): void => {
  showDeleteConfirmation.value = true;
};
</script>
